// SGR decoding for terminal transcripts — pure, and deliberately separate from
// the component that draws them.
//
// It lives in its own module for two reasons. The first is testability: the
// component module defines a custom element at import time and reaches for
// `document`, so a unit test of the parser would have to stand up a DOM just to
// ask what a byte sequence means. The second is that this is the half most
// likely to be wrong in a way nobody sees — an off-by-one in a parameter list
// silently drops a colour rather than throwing — so it is the half that wants
// assertions rather than screenshots.
//
// Only SGR (ESC [ … m) is decoded. Cursor moves, alt-screen switches and erase
// sequences belong to a live TUI repainting itself; a transcript containing them
// is a recording rather than a session, so they are dropped rather than guessed
// at.
//
// The vocabulary is tsh's own `SpanStyle` (`tento-term/src/span_style.rs`):
// the eight base colours, indexed and truecolour, and the full attribute set —
// bold, dim, italic, underline, strike, reverse — because the harness
// components use all of it and a partial decoder renders their output in the
// wrong voice.

/** One SGR state: what the escape codes in force at this point say. */
export interface Sgr {
  /** A CSS colour, already resolved. Null is the inherited ink. */
  fg: Option<string>;
  /**
   * A CSS colour for the ground behind the run. Null is the panel's own
   * ground, showing through.
   *
   * Background is not decoration in a TUI, it is structure: tsh paints its
   * status band, its mode badge and its selected rows entirely with it, so a
   * decoder that reads only foreground renders a badge as bare text on the
   * wrong ground and loses the band altogether.
   */
  bg: Option<string>;
  bold: boolean;
  dim: boolean;
  /**
   * The tail of tsh's own span vocabulary — `SpanStyle` in
   * `tento-term/src/span_style.rs` carries fg/bg/bold/dim/italic/underline/
   * strike/reverse, and the harness components use the tail: a thinking row
   * is italic, a completed todo is struck through. A decoder that stops at
   * bold and dim renders a claude lane with the wrong voice.
   */
  italic: boolean;
  underline: boolean;
  strike: boolean;
  /**
   * Ink and ground swapped. Kept as a flag rather than resolved here, because
   * the swap needs the DEFAULTS — a reverse run with no colours set paints
   * ground-on-ink — and only the painter knows what the inherited ink and the
   * panel's ground are.
   */
  reverse: boolean;
}

/** A run of characters sharing one SGR state. */
export interface Span {
  text: string;
  sgr: Sgr;
}

const SGR_SEQ = /\u001b\[([0-9;]*)m/g;
// Everything else an ESC can start, dropped whole. Three families beyond CSI
// appear in real pty captures: OSC (title writes, ESC ] ... BEL), two-byte
// charset/keypad selections (ESC ( B, ESC =), and bare final-byte escapes. A
// sweep that only knows CSI leaves the ESC byte AND the payload — a captured
// frame's window title — sitting in the transcript text.
const ANY_ESCAPE =
  /\u001b\[[0-9;?]*[A-Za-z]|\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)|\u001b[()#][0-9A-Za-z]|\u001b[A-Za-z=><\\]/g;
const ESC = "\u001b";

const BASE_NAMES = [
  "black",
  "red",
  "green",
  "yellow",
  "blue",
  "magenta",
  "cyan",
  "white",
];

// The xterm base and bright sets. Everything above 15 is computed rather than
// tabulated, so the table stays short and the arithmetic stays checkable.
const XTERM_BASE = [
  "#000000",
  "#cd3131",
  "#0dbc79",
  "#e5e510",
  "#2472c8",
  "#bc3fbc",
  "#11a8cd",
  "#e5e5e5",
  "#666666",
  "#f14c4c",
  "#23d18b",
  "#f5f543",
  "#3b8eea",
  "#d670d6",
  "#29b8db",
  "#ffffff",
];

/**
 * A named colour, emitted as a custom property so a design can restyle the
 * palette without the decoder knowing anything about the design. The xterm
 * value rides along as the fallback, so a caller that sets nothing still gets
 * the colour the terminal had.
 */
function namedColor(index: number, bright: boolean): string {
  const name = BASE_NAMES[index] ?? "white";
  const key = bright ? `bright-${name}` : name;
  return `var(--session-replay-ansi-${key}, ${XTERM_BASE[bright ? index + 8 : index]})`;
}

/**
 * The xterm 256 palette: 0-15 are the base and bright sets, 16-231 a 6x6x6
 * cube, 232-255 a 24-step grey ramp. tsh's own GRAY is index 238, which lands
 * in that last range — so a rule dimmed to 238 keeps the exact grey it had in
 * the terminal.
 */
export function xterm256(index: number): string {
  if (index < 16) return XTERM_BASE[index] ?? "#ffffff";
  if (index < 232) {
    const n = index - 16;
    const step = (v: number): number => (v === 0 ? 0 : 55 + v * 40);
    return `rgb(${step(Math.floor(n / 36))} ${step(Math.floor(n / 6) % 6)} ${step(n % 6)})`;
  }
  const level = 8 + (index - 232) * 10;
  return `rgb(${level} ${level} ${level})`;
}

export function blankSgr(): Sgr {
  return {
    fg: null,
    bg: null,
    bold: false,
    dim: false,
    italic: false,
    underline: false,
    strike: false,
    reverse: false,
  };
}

/** Fold one parameter list into the running state. */
function applySgr(state: Sgr, params: string): Sgr {
  // An empty parameter list is a reset: ESC [ m means ESC [ 0 m.
  const codes = params === "" ? [0] : params.split(";").map((n) => Number(n) || 0);
  let next: Sgr = { ...state };
  for (let i = 0; i < codes.length; i++) {
    const code = codes[i];
    if (code === 0) next = blankSgr();
    else if (code === 1) next.bold = true;
    else if (code === 2) next.dim = true;
    else if (code === 3) next.italic = true;
    else if (code === 4) next.underline = true;
    else if (code === 7) next.reverse = true;
    else if (code === 9) next.strike = true;
    else if (code === 22) {
      next.bold = false;
      next.dim = false;
    } else if (code === 23) next.italic = false;
    else if (code === 24) next.underline = false;
    else if (code === 27) next.reverse = false;
    else if (code === 29) next.strike = false;
    else if (code >= 30 && code <= 37) next.fg = namedColor(code - 30, false);
    else if (code === 39) next.fg = null;
    else if (code >= 90 && code <= 97) next.fg = namedColor(code - 90, true);
    // The background codes are the foreground ones offset by ten, and they
    // share the palette: a design that restyles --session-replay-ansi-blue
    // restyles it wherever it lands, ink or ground.
    else if (code >= 40 && code <= 47) next.bg = namedColor(code - 40, false);
    else if (code === 49) next.bg = null;
    else if (code >= 100 && code <= 107) next.bg = namedColor(code - 100, true);
    else if ((code === 38 || code === 48) && codes[i + 1] === 5) {
      // 38;5;N / 48;5;N — indexed. Both extra parameters belong to this code,
      // so the cursor skips them; leaving them to the next iteration would read
      // the index as a fresh SGR code and paint the rest of the line wrong.
      const color = xterm256(codes[i + 2] ?? 7);
      if (code === 38) next.fg = color;
      else next.bg = color;
      i += 2;
    } else if ((code === 38 || code === 48) && codes[i + 1] === 2) {
      const color = `rgb(${codes[i + 2] ?? 0} ${codes[i + 3] ?? 0} ${codes[i + 4] ?? 0})`;
      if (code === 38) next.fg = color;
      else next.bg = color;
      i += 4;
    }
  }
  return next;
}

/**
 * Cut one raw line into spans, and return its text with every escape removed.
 *
 * The plain `text` is not a convenience — it is what the typing animation
 * advances through and what a caller ignoring colour renders, so it must never
 * contain an escape byte. That is why the non-SGR sweep at the end runs over
 * both halves rather than only over the text.
 */
export function decodeAnsi(raw: string): { text: string; spans: Span[] } {
  SGR_SEQ.lastIndex = 0;
  const spans: Span[] = [];
  let state = blankSgr();
  let at = 0;
  let text = "";
  let match = SGR_SEQ.exec(raw);
  while (match !== null) {
    const chunk = raw.slice(at, match.index);
    if (chunk.length > 0) {
      spans.push({ text: chunk, sgr: state });
      text += chunk;
    }
    state = applySgr(state, match[1]);
    at = match.index + match[0].length;
    match = SGR_SEQ.exec(raw);
  }
  const tail = raw.slice(at);
  if (tail.length > 0) {
    spans.push({ text: tail, sgr: state });
    text += tail;
  }
  if (text.includes(ESC)) {
    text = text.replace(ANY_ESCAPE, "");
    for (const span of spans) span.text = span.text.replace(ANY_ESCAPE, "");
  }
  return { text, spans };
}
